import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, Wifi, Upload, Download, Shield, Clock } from 'lucide-react';
import { useDarkMode } from '../darkmode/DarkModeContext';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  'http://localhost:5000';

const Diskon = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // --- helpers untuk kecepatan dan spesifikasi teknis ---
  const resolveUpload = (pkg) => {
    if (pkg.upload) return pkg.upload;
    const v = pkg?.specifications?.uploadSpeed?.value;
    const u = pkg?.specifications?.uploadSpeed?.unit || 'Mbps';
    return typeof v === 'number' ? `${v} ${u}` : '-';
  };
  const resolveDownload = (pkg) => {
    if (pkg.speed) return pkg.speed;
    const v = pkg?.specifications?.downloadSpeed?.value;
    const u = pkg?.specifications?.downloadSpeed?.unit || 'Mbps';
    return typeof v === 'number' ? `${v} ${u}` : '-';
  };
  const resolveTechSpecs = (pkg) => {
    if (pkg.technicalSpecs && Object.keys(pkg.technicalSpecs).length) return pkg.technicalSpecs;
    const spec = pkg.specifications || {};
    const out = {};
    if (spec?.latency?.value != null) out['Latency'] = `< ${spec.latency.value}${spec.latency.unit || 'ms'}`;
    if (spec?.jitter?.value != null) out['Jitter'] = `< ${spec.jitter.value}${spec.jitter.unit || 'ms'}`;
    if (spec?.packetLoss?.value != null) out['Packet Loss'] = `< ${spec.packetLoss.value}${spec.packetLoss.unit || '%'}`;
    return out;
  };

  // --- fetch dari API ---
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await fetch(`${API_BASE}/api/packages?category=home`, { signal: ac.signal });
        const json = await res.json();
        if (!json?.success) throw new Error(json?.message || 'Gagal mengambil paket');
        setPackages(Array.isArray(json.data) ? json.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message || 'Terjadi kesalahan jaringan');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const currentPackages = useMemo(() => packages, [packages]);

  const handlePackageSelect = (pkg) =>
    setSelectedPackage({
      ...pkg,
      _upload: resolveUpload(pkg),
      _download: resolveDownload(pkg),
      _tech: resolveTechSpecs(pkg),
    });
  const closePackageDetail = () => setSelectedPackage(null);

  const normalizeForCheckout = (pkg) => {
    const id = pkg._id || pkg.id || `pkg_${Date.now()}`;
    return {
      _id: id,
      id,
      name: pkg.name,
      speed: resolveDownload(pkg),
      upload: resolveUpload(pkg),
      price: 'Hubungi kami untuk informasi harga',
      features: pkg.features || [],
      category: pkg.category || 'Home',
      color: pkg.color,
    };
  };

  const handleGoToCoverage = (pkg) => {
    const normalized = normalizeForCheckout(pkg);
    navigate('/coverage-check', { state: { selectedPackage: normalized } });
  };

  return (
    <div
      className="bg-cover bg-center transition-colors duration-300"
      style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
      }}
    >
      {/* Scrollbar styles */}
      <style>
        {`
          /* Scrollbar styling for webkit browsers */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkMode ? '#374151' : '#e5e7eb'};
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkMode ? '#1E9B4C' : '#10b981'};
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkMode ? '#15803d' : '#059669'};
          }
          /* Scrollbar styling for Firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: ${isDarkMode ? '#1E9B4C #374151' : '#10b981 #e5e7eb'};
          }
          /* Smooth scrolling */
          .smooth-scroll {
            scroll-behavior: smooth;
          }
        `}
      </style>

      {/* Packages Section */}
      <section className="py-8 sm:py-12 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className={`text-2xl sm:text-3xl font-bold mb-4 transition-colors ${
                isDarkMode ? 'text-green-50' : 'text-gray-900'
              }`}
            >
              Paket Home
            </h2>
            <p className={`text-base sm:text-lg transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pilih paket yang sesuai dengan kebutuhan Anda
            </p>
          </div>

          {loading && (
            <div className={`text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Memuat paket…</div>
          )}
          {err && <div className="text-center text-red-500">{err}</div>}

          {!loading && !err && (
            <div className="overflow-x-auto custom-scrollbar smooth-scroll pb-4">
              <div className="flex gap-4 sm:gap-6 lg:gap-8" style={{ width: 'max-content' }}>
                {currentPackages.map((pkg) => {
                  const uploadText = resolveUpload(pkg);
                  const downloadText = resolveDownload(pkg);

                  return (
                    <div
                      key={pkg._id}
                      className={`relative rounded-2xl p-6 sm:p-8 shadow-lg transition-all duration-300 hover:scale-105 flex-shrink-0 w-72 sm:w-80 lg:w-96 ${
                        pkg.popular
                          ? `bg-white text-black shadow-2xl transform scale-105`
                          : isDarkMode
                          ? 'bg-gray-800 text-green-50 border border-gray-700 hover:shadow-green-700/20'
                          : 'bg-white text-gray-900 border border-gray-200 hover:shadow-green-700/10'
                      }`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                            Most Popular
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-xl sm:text-2xl font-bold mb-2">{pkg.name}</h3>
                        <div className="mb-4">
                          <span className="text-lg sm:text-xl font-semibold">Hubungi kami untuk informasi harga</span>
                        </div>
                      </div>

                      <div className="mb-8">
                        <div className="flex items-center justify-between mb-2 text-sm sm:text-base">
                          <span className="flex items-center gap-1">
                            <Download className="w-4 h-4" />
                            Download
                          </span>
                          <span className="font-bold">{downloadText}</span>
                        </div>
                        <div className="flex items-center justify-between mb-4 text-sm sm:text-base">
                          <span className="flex items-center gap-1">
                            <Upload className="w-4 h-4" />
                            Upload
                          </span>
                          <span className="font-bold">{uploadText}</span>
                        </div>

                        <ul className="space-y-2 max-h-36 sm:max-h-44 overflow-y-auto custom-scrollbar text-xs sm:text-sm">
                          {(pkg.features || []).map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button
                        onClick={() => handlePackageSelect(pkg)}
                        className={`w-full py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold transition-colors duration-200 text-sm sm:text-base ${
                          pkg.popular ? 'bg-[#1E9B4C] text-white hover:bg-green-700' : 'bg-[#1E9B4C] text-white hover:bg-green-700'
                        }`}
                      >
                        Pilih Paket
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && !err && currentPackages.length > 0 && (
            <div className="text-center mt-4 sm:hidden">
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ← Geser untuk melihat paket lainnya →
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Modal Detail Paket */}
      {selectedPackage && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[2001]">
          <div
            className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl transition-colors duration-300 custom-scrollbar ${
              isDarkMode ? 'bg-gray-800 text-green-50' : 'bg-white text-gray-900'
            }`}
          >
            <div
              className={`sticky top-0 flex justify-between items-center p-4 sm:p-6 border-b transition-colors duration-300 ${
                isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <h3 className="text-xl sm:text-2xl font-bold pr-4">Detail Paket {selectedPackage.name}</h3>
              <button
                onClick={closePackageDetail}
                className={`p-2 rounded-lg transition-colors duration-300 flex-shrink-0 ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 sm:w-6 h-5 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Overview */}
                <div>
                  <div
                    className={`p-4 sm:p-6 rounded-xl mb-6 bg-gradient-to-br ${
                      selectedPackage.color || 'from-green-500 to-green-700'
                    } text-white`}
                  >
                    <h4 className="text-lg sm:text-xl font-bold mb-2">{selectedPackage.name}</h4>
                    <div className="mb-4">
                      <span className="text-lg sm:text-xl font-semibold">Hubungi kami untuk informasi harga</span>
                    </div>
                    <p className="opacity-90 text-sm sm:text-base">{selectedPackage.description}</p>
                  </div>

                  <div
                    className={`p-4 sm:p-6 rounded-xl transition-colors duration-300 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Wifi className="w-4 sm:w-5 h-4 sm:h-5" />
                      <span className="text-sm sm:text-base">Kecepatan Internet</span>
                    </h5>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span>Download Speed</span>
                        <span className="font-bold text-[#1E9B4C]">{selectedPackage._download || resolveDownload(selectedPackage)}</span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base">
                        <span>Upload Speed</span>
                        <span className="font-bold text-[#1E9B4C]">{selectedPackage._upload || resolveUpload(selectedPackage)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features & Specs */}
                <div className="space-y-6">
                  <div
                    className={`p-4 sm:p-6 rounded-xl transition-colors duration-300 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-[#1E9B4C]" />
                      <span className="text-sm sm:text-base">Fitur Unggulan</span>
                    </h5>
                    <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar text-xs sm:text-sm">
                      {(selectedPackage.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-3 sm:w-4 h-3 sm:h-4 text-[#1E9B4C] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={`p-4 sm:p-6 rounded-xl transition-colors duration-300 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}
                  >
                    <h5 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-[#1E9B4C]" />
                      <span className="text-sm sm:text-base">Spesifikasi Teknis</span>
                    </h5>
                    <div className="space-y-3 text-sm sm:text-base max-h-48 overflow-y-auto custom-scrollbar">
                      {Object.entries(selectedPackage._tech || resolveTechSpecs(selectedPackage)).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span>{key}</span>
                          <span className="font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleGoToCoverage(selectedPackage)}
                  className="flex-1 bg-[#1E9B4C] text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors duration-300 text-sm sm:text-base"
                >
                  Berlangganan Sekarang
                </button>
                <button
                  className={`px-6 py-3 rounded-lg font-semibold border transition-colors duration-300 text-sm sm:text-base ${
                    isDarkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Konsultasi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Diskon;